import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    selectRenamingLayerId,
    selectSelectedLayerIds,
    setActiveFeatureId,
    setActiveLayerId,
    setRenamingLayerId,
} from "../slice.new"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"
import { RenamableLabel } from "@nova/component-renamable-label"
import { selectAncestorEntitiesByEntityId } from "../slice"
import type { PluginSpec } from "plugins/plugin-registry"
import { getGenericUseSidePanelListItemActions } from "./genericListItemActions"
import { getGenericListItemProgress } from "./GenericLayerListItemProgress"
import { getSidePanelFeatureFromLayerType } from "../constants"
import { useProgressDecisions } from "../progress/useProgressDecisions"
import { PortalledTooltip } from "features/popups/PortalledTooltip"
import { useResizableSidePanelWidth } from "../SidePanel"
import { useIsEditor } from "tools/hooks/useIsEditor"

export type SidePanelListItemAction =
    | {
          icon: Icons.IconComponent
          onClick: (id: string) => void
          keepVisible?: boolean
          className?: string
          isPlaceholder?: false
      }
    | {
          icon?: never
          onClick?: never
          keepVisible?: never
          className?: never
          isPlaceholder: true
      }

export function getGenericLayerListItem({
    spec,
    ProgressComponent = !!spec.useUploadStatusAndProgress
        ? getGenericListItemProgress({
              useStatusAndProgress: spec.useUploadStatusAndProgress,
          })
        : undefined,
    useActions = getGenericUseSidePanelListItemActions(spec),
    LayerNameComponent = getGenericLayerNameComponent(spec),
    LayerIconComponent = getGenericLayerIconComponent(spec),
    RightSlotComponent,
    onClick = () => {},
}: {
    spec: PluginSpec
    ProgressComponent?: FC<{ id: string; isVisible: boolean }>
    useActions?: (id: string) => SidePanelListItemAction[]
    LayerNameComponent?: FC<{ id: string }>
    LayerIconComponent?: FC<{ id: string }>
    RightSlotComponent?: FC<{ id: string }>
    onClick?: (a: { id: string; e: React.MouseEvent }) => void
}) {
    const GenericLayerListItem = ({ id, isHovered }: { id: string; isHovered: boolean }) => {
        const dispatch = useAppDispatch()
        const isEditor = useIsEditor()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(id))
        const isVisible = entity.visible && !ancestors.find((ancestor) => !ancestor.visible)
        const renamingLayerId = useAppSelector(selectRenamingLayerId)
        const isRenaming = renamingLayerId === id

        const { canActivateFeature } = useProgressDecisions({
            id,
            useHook: spec.useUploadStatusAndProgress,
        })

        return (
            <div
                className={classNames("flex gap-2 w-full px-2 py-[6px] items-stretch", {
                    "items-center": !ProgressComponent,
                })}
                data-drag-type="touch"
                onClick={(e) => {
                    if (!e.shiftKey && !e.metaKey && !e.ctrlKey && canActivateFeature) {
                        dispatch(setActiveLayerId({ id }))
                        dispatch(
                            setActiveFeatureId({
                                feature: getSidePanelFeatureFromLayerType(spec.sidePanelLayerType),
                            })
                        )
                    }
                    onClick({ id, e })
                }}
                onDoubleClick={() => {
                    if (!isEditor) return
                    dispatch(setRenamingLayerId({ id }))
                }}
            >
                <div className="h-full">
                    <LayerIconComponent id={id} />
                </div>
                <div className="w-full flex flex-col gap-2 relative shrink min-w-0 justify-center">
                    <div className="w-full flex gap-2 items-center justify-between shrink min-w-0 mr-1">
                        <LayerNameComponent id={id} />
                        {!isRenaming && (
                            <div className="flex items-center gap-1 shrink-0">
                                {RightSlotComponent && <RightSlotComponent id={id} />}
                                <LayerListItemActions
                                    id={id}
                                    useActions={useActions}
                                    isHovered={isHovered}
                                />
                            </div>
                        )}
                    </div>
                    {ProgressComponent && <ProgressComponent id={id} isVisible={!!isVisible} />}
                </div>
            </div>
        )
    }
    return GenericLayerListItem
}

export function getGenericLayerNameComponent(spec: PluginSpec) {
    const LayerNameComponent = ({
        id,
        size = "sm",
        useColorClassNames = true,
    }: {
        id: string
        size?: "sm" | "md"
        useColorClassNames?: boolean
    }) => {
        const dispatch = useAppDispatch()
        const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
        const isSelected = useMemo(() => selectedLayerIds.includes(id), [id, selectedLayerIds])
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(id))
        const isVisible = entity.visible && !ancestors.find((ancestor) => !ancestor.visible)
        const renamingLayerId = useAppSelector(selectRenamingLayerId)
        const isRenaming = renamingLayerId === id
        const colorsClassNames = classNames({
            "!text-gray-50": isVisible && !isSelected,
            "!text-white": isVisible && isSelected,
            "!text-[#666666]": !isVisible,
        })
        const sidePanelWidth = useResizableSidePanelWidth()
        const nameRef = useRef<HTMLDivElement>(null)
        const [isNameTruncated, setIsNameTruncated] = useState(false)
        useEffect(() => {
            if (nameRef.current) {
                setIsNameTruncated(nameRef.current.clientWidth < nameRef.current.scrollWidth)
            }
            setIsHovering(false)
        }, [isRenaming, sidePanelWidth])
        const mouseEnterTimeoutRef = useRef<NodeJS.Timeout | null>(null)
        const [isHovering, setIsHovering] = useState(false)
        const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
        const handleMouseMove = useCallback((e: MouseEvent) => {
            setMousePos({ x: e.clientX, y: e.clientY })
        }, [])
        const handleMouseEnter = useCallback(() => {
            if (!mouseEnterTimeoutRef.current) {
                mouseEnterTimeoutRef.current = setTimeout(() => setIsHovering(true), 500)
            }
        }, [])
        const handleMouseLeave = useCallback(() => {
            if (mouseEnterTimeoutRef.current) {
                clearTimeout(mouseEnterTimeoutRef.current)
                mouseEnterTimeoutRef.current = null
            }
            setIsHovering(false)
        }, [])
        return (
            <div
                ref={nameRef}
                className={classNames("w-full max-w-full group/tooltip", {
                    "shrink truncate": !isRenaming,
                    [colorsClassNames]: useColorClassNames,
                    "!text-gray-0": !useColorClassNames,
                })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
            >
                <RenamableLabel
                    value={entity.name}
                    rename={isRenaming}
                    highlightOnRenameStart
                    setRename={
                        isRenaming
                            ? (r) => dispatch(setRenamingLayerId({ id: r ? id : undefined }))
                            : () => null
                    }
                    classNames={classNames("w-full select-none", {
                        [colorsClassNames]: useColorClassNames,
                        "!text-gray-0": !useColorClassNames,
                        "!text-[12px]": size === "sm",
                        "!text-[14px]": size === "md",
                    })}
                    inputClassNames={classNames("w-full !max-h-5", {
                        "!text-[12px]": size === "sm",
                        "!text-[14px]": size === "md",
                    })}
                    onRename={(name) => {
                        dispatch(spec.updateAction({ id, updates: { name } }))
                        dispatch(setRenamingLayerId({ id: undefined }))
                    }}
                    style={{
                        maxWidth: "100%",
                        minWidth: 10,
                    }}
                />
                {isNameTruncated && isHovering && (
                    <PortalledTooltip initialPosition={mousePos}>
                        <div className="bg-gray-900 rounded-lg border border-gray-700 px-2 py-1 shadow-tooltip">
                            <small className="text-gray-50">{entity.name}</small>
                        </div>
                    </PortalledTooltip>
                )}
            </div>
        )
    }
    return LayerNameComponent
}

export function getGenericLayerIconComponent(spec: PluginSpec) {
    const LayerIconComponent = ({ id }: { id: string }) => {
        const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
        const isSelected = useMemo(() => selectedLayerIds.includes(id), [id, selectedLayerIds])
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(id))
        const isVisible = entity.visible && !ancestors.find((ancestor) => !ancestor.visible)
        const isLayerExpanded = useLocalStorageValue("sidePanelExpandedLayerIds").includes(id)
        return (
            <div className="w-5 h-5 flex justify-center items-center">
                <Icons.SmallIcon
                    className="shrink-0"
                    Icon={
                        isLayerExpanded && spec.SecondaryIcon
                            ? spec.SecondaryIcon
                            : spec.PrimaryIcon
                    }
                    iconClassName={classNames({
                        "text-white-600": isVisible && isSelected,
                        "text-white-400": isVisible && !isSelected,
                        "text-white-200": !isVisible,
                    })}
                />
            </div>
        )
    }
    return LayerIconComponent
}

export function LayerListItemActions({
    id,
    useActions,
    isHovered,
}: {
    id: string
    useActions: (id: string) => SidePanelListItemAction[]
    isHovered: boolean
}) {
    const actions = useActions(id)
    if (!isHovered && actions.every((action) => !action.keepVisible)) return null
    return (
        <div className="flex gap-[6px]">
            {actions.map((action, i) => {
                const prevActions = actions.slice(0, i)
                if (
                    !isHovered &&
                    !action.keepVisible &&
                    !prevActions.find((a) => !!a.keepVisible)
                ) {
                    // Only render the actions to the right of an action that is visible
                    // Prevents ellipsis from showing up prematurely
                    return null
                }
                if (action.isPlaceholder) {
                    return <div key={i} className="w-4" />
                }
                return (
                    <Icons.ExtraSmallIcon
                        key={i}
                        Icon={action.icon}
                        className={classNames(
                            "text-white-400 hover:text-white-600",
                            action.className,
                            {
                                "opacity-0 pointer-events-none": !action.keepVisible && !isHovered,
                            }
                        )}
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            action.onClick(id)
                        }}
                    />
                )
            })}
        </div>
    )
}
